import { useState } from "react";
import { useAuth } from "@/auth";
import { useNavigate } from "react-router-dom";
import { Plane, Lock, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("admin@366transfer.com");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Willkommen zurück!");
            navigate("/");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Anmeldung fehlgeschlagen");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#f4f7f9]">
            {/* Left – Hero */}
            <div className="hidden lg:flex relative overflow-hidden bg-[#0f1a2e]">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: 'url(https://images.pexels.com/photos/19138473/pexels-photo-19138473.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1440)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0f1a2e]/95 via-[#1a2b48]/85 to-[#0f1a2e]/95" />
                <div className="absolute inset-0 topo-lines opacity-30" />

                <div className="relative z-10 flex flex-col justify-between p-14 text-white w-full">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center border border-white/15">
                            <Plane className="w-5 h-5 -rotate-12" strokeWidth={2} />
                        </div>
                        <div>
                            <div className="font-semibold tracking-tight text-[16px]">366Transfer</div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-blue-300/80">Outreach Suite</div>
                        </div>
                    </div>

                    <div className="space-y-6 max-w-md">
                        <div className="text-[11px] uppercase tracking-[0.3em] text-blue-300/80 font-semibold">Schweiz · Deutschland · Österreich</div>
                        <h1 className="font-display text-5xl xl:text-6xl leading-[1.05] text-white">
                            Premium-Transfers,<br />
                            <em className="text-blue-300/90">smart vermarktet.</em>
                        </h1>
                        <p className="text-blue-100/80 text-base leading-relaxed">
                            Erreichen Sie Reisebüros und Travel-Agenturen in der DACH-Region mit professionellen,
                            mehrsprachigen Outreach-Kampagnen. Verfasst, versendet und gemessen — alles aus einer Hand.
                        </p>
                        <div className="flex gap-6 pt-2 text-xs uppercase tracking-widest text-blue-200/70 font-semibold">
                            <span>· DE</span><span>· EN</span><span>· FR</span><span>· IT</span>
                        </div>
                    </div>

                    <div className="text-xs text-blue-200/60">© 2026 366Transfer.com — Alle Rechte vorbehalten.</div>
                </div>
            </div>

            {/* Right – Form */}
            <div className="flex items-center justify-center p-6 lg:p-12 topo-bg">
                <div className="w-full max-w-md animate-fade-in-up">
                    <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#007bff] to-[#1a2b48] flex items-center justify-center">
                            <Plane className="w-5 h-5 text-white -rotate-12" />
                        </div>
                        <div className="font-semibold text-[#1a2b48]">366Transfer Outreach</div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl shadow-[0_20px_70px_-30px_rgba(15,23,42,0.25)] p-10">
                        <div className="text-[11px] uppercase tracking-[0.25em] text-[#007bff] font-bold mb-2">Anmelden</div>
                        <h2 className="font-display text-3xl text-[#1a2b48] mb-1">Willkommen zurück.</h2>
                        <p className="text-slate-500 text-sm mb-8">Melden Sie sich an, um Ihre Outreach-Kampagnen zu verwalten.</p>

                        <form onSubmit={onSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">E-Mail</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        id="email"
                                        data-testid="login-email-input"
                                        type="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus-visible:ring-[#007bff]/30 focus-visible:border-[#007bff]"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Passwort</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        id="password"
                                        data-testid="login-password-input"
                                        type="password"
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 h-11 bg-slate-50 border-slate-200 focus-visible:ring-[#007bff]/30 focus-visible:border-[#007bff]"
                                        required
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                data-testid="login-submit-button"
                                disabled={loading}
                                className="w-full h-11 bg-[#007bff] hover:bg-[#0056b3] text-white font-medium tracking-wide shadow-md shadow-blue-500/20 transition-all hover:shadow-lg hover:shadow-blue-500/30"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Anmelden →"}
                            </Button>
                        </form>

                        <div className="mt-8 pt-6 border-t border-slate-100 text-xs text-slate-400 leading-relaxed">
                            <strong className="text-slate-600">Standardzugang:</strong> admin@366transfer.com<br />
                            Sicherer Zugang über JWT, geschütztes Backend.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
